import { PAYMENTS_ENABLED } from '../lib/environment';
import { usersApiUrl } from '../lib/serviceUrls';
import React, { useState } from 'react';
import { useAuth, useUser } from '@clerk/clerk-react';
import { SwissNotice, SwissPanel, SwissSectionHeader } from './swiss';

const OpenInNewTabIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-4 h-4 inline-block ml-1" aria-hidden="true">
    <path d="M10 6v2H5v11h11v-5h2v6a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V7a1 1 0 0 1 1-1h6zm11-3v8h-2V6.413l-7.793 7.794-1.414-1.414L17.585 5H13V3h8z" />
  </svg>
);

function formatPeriodEnd(value) {
  const seconds = Number(value);
  if (!Number.isFinite(seconds) || seconds <= 0) return null;
  const date = new Date(seconds * 1000);
  return Number.isNaN(date.getTime())
    ? null
    : date.toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' });
}

export const BillingPage = () => {
  const [isLoading, setIsLoading] = useState(false);
  const { user, isLoaded, isSignedIn } = useUser();
  const [error, setError] = useState(null);
  const { getToken } = useAuth();

  const isSubscribed = user?.publicMetadata?.subscriptionStatus === 'active';

  if (!PAYMENTS_ENABLED) {
    return <SwissPanel><SwissSectionHeader label="Development" title="Billing" />
      <SwissNotice>Checkout and billing are disabled until the Stripe sandbox is configured.</SwissNotice></SwissPanel>;
  }

  if (!isLoaded || !isSignedIn || !user) {
    return (
      <SwissPanel>
        <div className="ff-loading" role="status">
          <span className="ff-loading__spinner" aria-hidden="true" />
          <span>Loading billing details…</span>
        </div>
      </SwissPanel>
    );
  }

  const handleBillingPortal = async () => {
    setError(null);
    setIsLoading(true);
    try {
      const token = await getToken();
      const response = await fetch(usersApiUrl('/get-stripe-billing-portal'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({}),
      });

      if (!response.ok) {
        throw new Error('Failed to get billing portal URL');
      }

      const data = await response.json();
      window.open(data.url, '_blank', 'noopener,noreferrer');
    } catch (error) {
      console.error('Error accessing billing portal:', error);
      setError('Failed to access billing portal. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubscribe = () => { window.location.assign('/setup-account'); };

  const periodEnd = formatPeriodEnd(user.publicMetadata?.subscriptionEndDate);
  const cancelAtPeriodEnd = user.publicMetadata?.subscriptionCancelAtPeriodEnd;

  return (
    <SwissPanel className="ff-alert-panel">
      <SwissSectionHeader title="Billing" />
      {error && <div role="alert" className="ff-notice ff-notice--error mt-4">{error}</div>}

      {isSubscribed ? (
        <>
          <dl className="ff-account-details">
            {periodEnd && <div className="ff-account-detail-row"><dt>Current period ends</dt><dd>{periodEnd}</dd></div>}
            {typeof cancelAtPeriodEnd === 'boolean' && (
              <div className="ff-account-detail-row"><dt>Auto-renew</dt><dd>{cancelAtPeriodEnd ? 'Off' : 'On'}</dd></div>
            )}
          </dl>

          <SwissNotice className="mb-4">
            Manage your subscription, payment method and invoices in the Stripe billing portal.
          </SwissNotice>
          <button
            type="button"
            className="ff-button"
            onClick={handleBillingPortal}
            disabled={isLoading}
          >
            {isLoading ? 'Loading…' : (
              <>
                {error ? 'Retry billing portal' : 'Manage your subscription'}
                <OpenInNewTabIcon />
              </>
            )}
          </button>
        </>
      ) : (
        <>
          <SwissNotice className="mb-4 mt-4">
            You are not currently subscribed. Subscribe to get email and SMS deal alerts.
          </SwissNotice>
          <button
            type="button"
            className="ff-button"
            onClick={handleSubscribe}
            disabled={!PAYMENTS_ENABLED}
          >
            Subscribe now
          </button>
        </>
      )}
    </SwissPanel>
  );
};
